import { Field, Form } from 'react-final-form';
import { FormApi } from 'final-form';
import { PageTitle, Heading, Link as Href, Text } from 'components/typography';
import Alert from 'components/alert';
import Button from 'components/button';
import FormError from 'components/form/error';
import LabeledInput from 'components/form/input';
import { Link } from 'components/configurable-routing';
import { useCallback, useEffect, useState } from 'react';
import Select from 'components/form/select';
import TextArea from 'components/form/text-area';
import topics from './topics';
import { Box, Flex, Grid } from 'components/box';
import contactUsMutation from 'mutations/contactUs.graphql';
import {
  MutationContactUsArgs,
  MutationContactUsOutput,
  ContactUsInput,
} from 'types/types';
import { useMutation } from '@apollo/client';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import config from '../../util/load-config';
import { css } from '@emotion/react';
import dynamic from 'next/dynamic';
import isEmail from 'sane-email-validation';
import { Check as CheckIcon } from 'react-feather';

const Modal = dynamic(
  () => import('components/_shared/widgets/modal/with-close')
);

let timeoutId;

const orderRequiredForTopics = topics.flatMap(topic =>
  topic.children
    .filter(child => child.orderNumberRequired)
    .map(child => child.value)
);

const ContactUs = ({ isFaq }: { isFaq?: boolean }) => {
  const [error, setError] = useState('');
  const [refreshCounter, setRefreshCounter] = useState(100);
  const [formApi, setFormApi] = useState<FormApi<ContactUsInput> | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptcha, setRecaptcha] = useState('');

  const [contactUs, { loading, data, error: contactUsError }] = useMutation<
    MutationContactUsOutput,
    MutationContactUsArgs
  >(contactUsMutation);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCounter(counter => counter - 1);
    }, 115000);
    return () => clearInterval(interval);
  }, []);

  const closeModal = useCallback(() => {
    setSuccessModalOpen(false);
    if (formApi) {
      formApi.reset();

      /**
       * final-form doesn't reset the field state when you reset the form
       * nor does it offer a single method for resetting all form field states
       */
      (formApi.getRegisteredFields() || []).forEach(field => {
        // @ts-ignore
        formApi.resetFieldState(field);
      });
    }
  }, [formApi]);

  useEffect(() => {
    /**
     * This check is only here so the "data" dependency is not flagged as unnecessary.
     * The idea is that after every submission or error the recaptcha is regenerated.
     */
    if (data || !data || refreshCounter) {
      const asyncRecaptcha = async () => {
        !!executeRecaptcha && setRecaptcha(await executeRecaptcha('contactus'));
      };
      asyncRecaptcha()
        .then()
        .catch(() => setRefreshCounter(counter => counter - 1));
    }
  }, [executeRecaptcha, error, data, refreshCounter]);

  useEffect(() => {
    if (data?.contactUs.message) {
      setSuccessModalOpen(true);
      setError('');
      timeoutId = setTimeout(() => {
        closeModal();
      }, 5000);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [data, closeModal, setError, data?.contactUs.message]);

  useEffect(() => {
    if (contactUsError) {
      const [firstError] = contactUsError.graphQLErrors;
      setError(firstError.message);
    }
  }, [contactUsError]);

  return (
    <>
      <PageTitle textAlign={isFaq ? ['center', 'left'] : 'center'} mb={4}>
        How can we help you?
      </PageTitle>

      {error && (
        <Alert p={2} mb={4} fontSize="regular" variant="error" lineHeight={1.3}>
          {error}
        </Alert>
      )}

      <Form<ContactUsInput>
        onSubmit={(values, form) => {
          const { name, email, order, regarding, question, details } = values;
          setFormApi(form);
          contactUs({
            variables: {
              input: {
                name,
                email,
                order,
                regarding,
                question,
                details,
                recaptcha,
              },
            },
          });
        }}
      >
        {({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Grid>
              {submitError && <FormError>{submitError}</FormError>}
              <Field
                name="name"
                validate={value => (!value ? 'Please enter your name' : null)}
              >
                {({ input, meta }) => (
                  <LabeledInput
                    {...input}
                    invalid={meta.error && meta.touched}
                    error={meta.error}
                    label="Full name"
                    placeholder="Enter your name"
                  />
                )}
              </Field>

              <Field
                name="email"
                validate={value => {
                  if (!value) {
                    return 'Please enter your email';
                  }
                  if (!isEmail(value)) {
                    return 'Email address is invalid';
                  }
                }}
              >
                {({ input, meta }) => (
                  <LabeledInput
                    {...input}
                    label="Email"
                    invalid={meta.error && meta.touched}
                    error={meta.error}
                    placeholder="Enter your email"
                    type="email"
                  />
                )}
              </Field>

              <Field
                name="regarding"
                validate={value => (!value ? 'Please select a topic' : null)}
              >
                {({ input, meta }) => (
                  <Box>
                    <Select
                      {...input}
                      label="Regarding"
                      invalid={meta.error && meta.touched}
                    >
                      <option>Select a topic</option>
                      {topics.map(topic => (
                        <optgroup key={topic.key} label={topic.title}>
                          {topic.children.map(child => (
                            <option key={child.value} value={child.value}>
                              {child.title}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </Select>
                    {meta.error && meta.touched && (
                      <FormError>{meta.error}</FormError>
                    )}
                  </Box>
                )}
              </Field>

              <Field
                name="order"
                validate={(value, allValues) => {
                  if (
                    !value &&
                    allValues.hasOwnProperty('regarding') &&
                    orderRequiredForTopics.includes(allValues['regarding'])
                  ) {
                    return 'Please enter your order number';
                  }
                  if (value && value.length < 7) {
                    return 'Please check that you have the correct order number';
                  }
                  if (value && value.match(/(\D)/g)) {
                    return 'Please remove all special characters and spaces from your order number';
                  }
                }}
              >
                {({ input, meta }) => (
                  <LabeledInput
                    {...input}
                    label="Order number"
                    placeholder="Enter your order number"
                    invalid={meta.error && meta.touched}
                    error={meta.error}
                  />
                )}
              </Field>

              <Field
                name="question"
                validate={value =>
                  !value ? 'Please add your question here' : null
                }
              >
                {({ input, meta }) => (
                  <LabeledInput
                    {...input}
                    label="Question"
                    invalid={meta.error && meta.touched}
                    error={meta.error}
                    placeholder="Question"
                  />
                )}
              </Field>

              <Field
                name="details"
                validate={value =>
                  !value ? 'Please describe your problem' : null
                }
              >
                {({ input, meta }) => (
                  <Box>
                    <TextArea
                      {...input}
                      placeholder="Enter more details"
                      label="Details"
                      invalid={meta.error && meta.touched}
                    />
                    {meta.error && meta.touched && (
                      <FormError>{meta.error}</FormError>
                    )}
                  </Box>
                )}
              </Field>

              <Box>
                <Text
                  textAlign={isFaq ? 'left' : 'center'}
                  fontSize="s"
                  color="darkishGrey"
                  lineHeight={1.5}
                >
                  This site is protected by reCAPTCHA and the Google{' '}
                  <Href href="https://policies.google.com/privacy">
                    Privacy Policy
                  </Href>{' '}
                  and{' '}
                  <Href href="https://policies.google.com/terms">
                    Terms of Service
                  </Href>{' '}
                  apply.
                </Text>
              </Box>

              <Button
                mt={3}
                width={['100%', null, 'auto']}
                loading={submitting || loading}
                disabled={submitting || loading}
                type="submit"
              >
                {loading ? 'Sending' : 'Send Message'}
              </Button>
            </Grid>
          </form>
        )}
      </Form>

      {!isFaq && (
        <Box mt={4}>
          <Text textAlign="center" lineHeight={1.5}>
            You can check our{' '}
            <Link target="_blank" href={config.helpCentreLinks.faq}>
              FAQ page
            </Link>{' '}
            for more information.
          </Text>
        </Box>
      )}

      <Modal close={() => closeModal()} isOpen={successModalOpen} size="small">
        <Box
          p={[1, null, 5]}
          pb={[4, null, 5]}
          css={css`
            text-align: center;
          `}
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            css={theme => css`
              border-radius: 100%;
              width: 140px;
              height: 140px;
              margin: 0 auto;
              background-color: ${theme.colors.lightLime};
              border: 1px solid ${theme.colors.lime};
            `}
          >
            <CheckIcon
              css={theme => css`
                color: ${theme.colors.lime};
                width: 95px;
                height: 95px;
              `}
            />
          </Flex>
          <Heading mt={4}>Your message was sent successfully.</Heading>
        </Box>
      </Modal>
    </>
  );
};

const ContactUsWrapper = ({ ...props }) => (
  <GoogleReCaptchaProvider reCaptchaKey={config.recaptcha.key}>
    <ContactUs {...props} />
  </GoogleReCaptchaProvider>
);

export default ContactUsWrapper;
